import { Form, Formik, type FormikHelpers } from "formik";
import { useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { changePassword } from "@/features/settings/api";
import { FIELD_LABEL } from "@/features/settings/components/settings-section";
import { ApiError } from "@/lib/api";

const securitySchema = Yup.object({
  currentPassword: Yup.string().required("Enter your current password"),
  newPassword: Yup.string()
    .min(8, "Min 8 characters")
    .required("Enter a new password")
    .notOneOf([Yup.ref("currentPassword")], "New password must differ from the current one"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords don't match")
    .required("Confirm your new password"),
});

interface SecurityValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function SecurityForm() {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(
    values: SecurityValues,
    { setFieldError }: FormikHelpers<SecurityValues>,
  ) {
    try {
      await changePassword(values.currentPassword, values.newPassword);
      // The server blacklists every existing refresh token on a password
      // change, so the current session is no longer valid — clear it and send
      // the user back to sign in with their new password.
      useAuthStore.getState().clearSession();
      toast.success("Password changed — please sign in again");
      navigate("/login", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // Surface a wrong current password on the field it belongs to.
        const data = err.data as Record<string, unknown> | null;
        if (data && typeof data === "object" && "current_password" in data) {
          setFieldError("currentPassword", "Current password is incorrect");
          return;
        }
        toast.error(err.message);
        return;
      }
      toast.error("Couldn't change password.");
    }
  }

  const toggle = (
    <button
      type="button"
      onClick={() => setShow((v) => !v)}
      className="text-muted-foreground hover:text-foreground transition-colors"
      aria-label={show ? "Hide passwords" : "Show passwords"}
    >
      {show ? <FiEye className="size-4" /> : <FiEyeOff className="size-4" />}
    </button>
  );

  return (
    <Formik<SecurityValues>
      initialValues={{ currentPassword: "", newPassword: "", confirmPassword: "" }}
      validationSchema={securitySchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <Card id="security" className="scroll-mt-24 gap-5 p-6">
            <h2 className="text-[17px] font-semibold tracking-tight">Security</h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              <FormField
                name="currentPassword"
                label="Current password"
                type={show ? "text" : "password"}
                autoComplete="current-password"
                endAdornment={toggle}
                labelClassName={FIELD_LABEL}
              />
              <FormField
                name="newPassword"
                label="New password"
                placeholder="At least 8 characters"
                type={show ? "text" : "password"}
                autoComplete="new-password"
                labelClassName={FIELD_LABEL}
              />
              <FormField
                name="confirmPassword"
                label="Confirm new password"
                placeholder="Repeat it"
                type={show ? "text" : "password"}
                autoComplete="new-password"
                labelClassName={FIELD_LABEL}
              />
            </div>
            <p className="text-muted-foreground text-xs">
              Changing your password signs out every other device.
            </p>
            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving…" : "Change password"}
              </Button>
            </div>
          </Card>
        </Form>
      )}
    </Formik>
  );
}
